import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import { ConfirmModal } from './ConfirmModal.tsx';
import { InvoiceHistoryList } from './InvoiceHistoryList.tsx';
import { NativeSelect } from './NativeSelect.tsx';
import { StatusMessage } from './StatusMessage.tsx';
import { WorkspaceContainer } from './WorkspaceLayout.tsx';
import {
  validateInvoiceWorkspaceSearch,
  type InvoiceWorkspaceSearchInput,
  type InvoiceWorkspaceSearch,
} from './invoiceWorkspaceSearch.ts';
import {
  fetchInvoiceDetailForDownload,
  invoiceDetailQueryKey,
  useDeleteInvoice,
  useDuplicateInvoice,
  useUpdateInvoicePaymentStatus,
  useInvoiceSearch,
  useInvoices,
  type InvoiceListFilters,
} from '../lib/invoices.ts';
import { downloadInvoicePdf, PDF_GENERATION_ERROR } from '../pdf/download.ts';
import { invoiceDetailToPdfModel } from '../pdf/model.ts';
import { loadPdfSignature } from '../pdf/signature.ts';
import {
  INVOICE_DELETE_ERROR,
  INVOICE_DUPLICATE_ERROR,
  INVOICE_LOAD_ERROR,
  INVOICE_PAYMENT_STATUS_ERROR,
  type Currency,
  type InvoiceCurrencyTotals,
  type InvoiceListItem,
  type InvoiceListResponse,
} from '../../shared/invoice.ts';
import { formatCurrencyCents } from '../../shared/format.ts';

export function InvoiceWorkspace(): React.ReactElement {
  const rawSearch = useRouterState({ select: (state) => state.location.search });
  const filters = validateInvoiceWorkspaceSearch(rawSearch as InvoiceWorkspaceSearchInput);
  const navigate = useNavigate();
  const controller = useInvoiceWorkspaceController(filters, navigate);

  useSanitizedInvoiceWorkspaceSearch(rawSearch as Record<string, unknown>, filters, navigate);

  return <InvoiceWorkspaceView filters={filters} controller={controller} />;
}

function InvoiceWorkspaceView({
  filters,
  controller,
}: {
  filters: InvoiceWorkspaceSearch;
  controller: InvoiceWorkspaceController;
}): React.ReactElement {
  const trimmedSearch = controller.freeTextSearch.trim();

  return (
    <div className="min-h-screen">
      <WorkspaceContainer as="main" className="py-8">
        <div className="space-y-6">
          <InvoiceWorkspaceHeader />
          <InvoiceWorkspaceFilters
            filters={filters}
            search={controller.freeTextSearch}
            onSearchChange={controller.changeFreeTextSearch}
            onFiltersChange={controller.updateFilters}
          />
          <InvoiceWorkspaceStatus
            message={controller.actionMessage}
            error={controller.actionError}
          />
          <InvoiceWorkspaceResults filters={filters} q={trimmedSearch} controller={controller} />
        </div>
      </WorkspaceContainer>
      <DeleteInvoiceConfirmation
        invoice={controller.selectedForDelete}
        confirming={controller.deletePending}
        onCancel={controller.cancelDelete}
        onConfirm={controller.confirmDelete}
      />
    </div>
  );
}

function InvoiceWorkspaceHeader(): React.ReactElement {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <h1 className="font-serif text-3xl italic text-ink sm:text-4xl">My Invoices</h1>
      <Link
        to="/invoices/new"
        className="inline-flex min-h-11 shrink-0 items-center justify-center rounded-full bg-ochre px-5 py-2 text-sm font-semibold text-paper-soft shadow-[0_8px_20px_-12px_rgba(176,119,43,0.85)] transition hover:bg-ochre-deep sm:hidden"
      >
        New invoice
      </Link>
    </div>
  );
}

function InvoiceWorkspaceStatus({
  message,
  error,
}: {
  message: string | null;
  error: string | null;
}): React.ReactElement | null {
  if (message) return <StatusMessage tone="success">{message}</StatusMessage>;
  if (error) return <StatusMessage tone="error">{error}</StatusMessage>;
  return null;
}

function DeleteInvoiceConfirmation({
  invoice,
  confirming,
  onCancel,
  onConfirm,
}: {
  invoice: InvoiceListItem | null;
  confirming: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}): React.ReactElement {
  return (
    <ConfirmModal
      open={invoice !== null}
      title="Delete invoice"
      confirmLabel="Delete invoice"
      confirming={confirming}
      onCancel={onCancel}
      onConfirm={onConfirm}
    >
      <p>
        Delete <span className="font-medium">Invoice #{invoice?.number}</span>? This cannot be
        undone.
      </p>
    </ConfirmModal>
  );
}

interface InvoiceWorkspaceController {
  freeTextSearch: string;
  changeFreeTextSearch: (value: string) => void;
  selectedForDelete: InvoiceListItem | null;
  cancelDelete: () => void;
  confirmDelete: () => void;
  requestDelete: (invoice: InvoiceListItem) => void;
  actionBusy: boolean;
  actionError: string | null;
  actionMessage: string | null;
  updateFilters: (next: Partial<InvoiceWorkspaceSearch>) => void;
  openInvoice: (invoice: InvoiceListItem) => void;
  duplicateSelected: (invoice: InvoiceListItem) => void;
  downloadSelected: (invoice: InvoiceListItem) => void;
  updatePaymentStatus: (invoice: InvoiceListItem, isPaid: boolean) => void;
  downloadPendingIds: ReadonlySet<string>;
  paymentPendingIds: ReadonlySet<string>;
  deletePending: boolean;
}

function useInvoiceWorkspaceController(
  filters: InvoiceWorkspaceSearch,
  navigate: ReturnType<typeof useNavigate>,
): InvoiceWorkspaceController {
  const queryClient = useQueryClient();
  const messages = useActionMessages();
  const navigation = useInvoiceWorkspaceNavigation(filters, navigate);
  const duplicateInvoice = useDuplicateInvoice();
  const deleteInvoice = useDeleteInvoice();
  const updatePaymentStatus = useUpdateInvoicePaymentStatus();
  const deleteAction = useDeleteInvoiceAction(deleteInvoice, messages);
  const renderedPdf = useRenderedPdfDownload(queryClient, messages);
  const paymentAction = usePaymentStatusAction(updatePaymentStatus, messages);
  const [freeTextSearch, setFreeTextSearch] = useState('');

  return {
    ...messages.state,
    ...navigation,
    ...deleteAction,
    freeTextSearch,
    changeFreeTextSearch: (value) => {
      setFreeTextSearch(value);
      messages.clearActionState();
    },
    actionBusy: duplicateInvoice.isPending || deleteInvoice.isPending,
    duplicateSelected: (invoice) => {
      void duplicateInvoiceSelected(invoice, duplicateInvoice.mutateAsync, navigate, messages);
    },
    downloadSelected: renderedPdf.downloadSelected,
    updatePaymentStatus: paymentAction.updatePaymentStatus,
    downloadPendingIds: renderedPdf.downloadPendingIds,
    paymentPendingIds: paymentAction.paymentPendingIds,
  };
}

interface ActionMessages {
  state: {
    actionError: string | null;
    actionMessage: string | null;
  };
  clearActionState: () => void;
  setActionError: (message: string) => void;
  setActionMessage: (message: string) => void;
}

function useActionMessages(): ActionMessages {
  const [actionError, setActionErrorState] = useState<string | null>(null);
  const [actionMessage, setActionMessageState] = useState<string | null>(null);

  return {
    state: { actionError, actionMessage },
    clearActionState: () => {
      setActionErrorState(null);
      setActionMessageState(null);
    },
    setActionError: (message) => {
      setActionMessageState(null);
      setActionErrorState(message);
    },
    setActionMessage: (message) => {
      setActionErrorState(null);
      setActionMessageState(message);
    },
  };
}

function useInvoiceWorkspaceNavigation(
  filters: InvoiceWorkspaceSearch,
  navigate: ReturnType<typeof useNavigate>,
): Pick<InvoiceWorkspaceController, 'updateFilters' | 'openInvoice'> {
  return {
    updateFilters: (next) => {
      const merged = sanitizeSearch({ ...filters, ...next, page: next.page ?? 1 });
      void navigate({ to: '/' as never, search: merged as never, replace: true });
    },
    openInvoice: (invoice) => {
      void navigate({ to: `/invoices/${encodeURIComponent(invoice.id)}` as never });
    },
  };
}

function useDeleteInvoiceAction(
  deleteInvoice: ReturnType<typeof useDeleteInvoice>,
  messages: ActionMessages,
): Pick<
  InvoiceWorkspaceController,
  'selectedForDelete' | 'cancelDelete' | 'confirmDelete' | 'requestDelete' | 'deletePending'
> {
  const [selectedForDelete, setSelectedForDelete] = useState<InvoiceListItem | null>(null);

  return {
    selectedForDelete,
    cancelDelete: () => setSelectedForDelete(null),
    deletePending: deleteInvoice.isPending,
    requestDelete: (invoice) => {
      messages.clearActionState();
      setSelectedForDelete(invoice);
    },
    confirmDelete: () => {
      void confirmDeleteInvoice(
        selectedForDelete,
        deleteInvoice.mutateAsync,
        setSelectedForDelete,
        messages,
      );
    },
  };
}

async function confirmDeleteInvoice(
  invoice: InvoiceListItem | null,
  deleteInvoice: (id: string) => Promise<void>,
  setSelectedForDelete: (invoice: InvoiceListItem | null) => void,
  messages: ActionMessages,
): Promise<void> {
  if (!invoice) return;
  messages.clearActionState();
  try {
    await deleteInvoice(invoice.id);
    setSelectedForDelete(null);
    messages.setActionMessage('Invoice deleted.');
  } catch {
    setSelectedForDelete(null);
    messages.setActionError(INVOICE_DELETE_ERROR);
  }
}

async function duplicateInvoiceSelected(
  invoice: InvoiceListItem,
  duplicateInvoice: (id: string) => Promise<{ invoice: { id: string } }>,
  navigate: ReturnType<typeof useNavigate>,
  messages: ActionMessages,
): Promise<void> {
  messages.clearActionState();
  try {
    const response = await duplicateInvoice(invoice.id);
    void navigate({ to: `/invoices/${encodeURIComponent(response.invoice.id)}` as never });
  } catch {
    messages.setActionError(INVOICE_DUPLICATE_ERROR);
  }
}

function useRenderedPdfDownload(
  queryClient: ReturnType<typeof useQueryClient>,
  messages: ActionMessages,
): Pick<InvoiceWorkspaceController, 'downloadSelected' | 'downloadPendingIds'> {
  const pending = usePendingIdSet();

  return {
    downloadPendingIds: pending.ids,
    downloadSelected: (invoice) => {
      void downloadRenderedPdf(
        invoice,
        queryClient,
        pending.hasPending,
        pending.setPending,
        messages,
      );
    },
  };
}

async function downloadRenderedPdf(
  invoice: InvoiceListItem,
  queryClient: ReturnType<typeof useQueryClient>,
  hasPending: (id: string) => boolean,
  setPending: (id: string, pending: boolean) => void,
  messages: ActionMessages,
): Promise<void> {
  if (hasPending(invoice.id)) return;
  setPending(invoice.id, true);
  messages.clearActionState();
  try {
    const response = await queryClient.fetchQuery({
      queryKey: invoiceDetailQueryKey(invoice.id),
      queryFn: () => fetchInvoiceDetailForDownload(invoice.id),
      staleTime: 0,
    });
    const model = invoiceDetailToPdfModel(response.invoice);
    const signature = await safeLoadPdfSignature(model.sender.signaturePreviewUrl);
    const result = await downloadInvoicePdf({ invoice: model, signature });
    if (!result.ok) messages.setActionError(result.error);
  } catch {
    messages.setActionError(PDF_GENERATION_ERROR);
  } finally {
    setPending(invoice.id, false);
  }
}

function usePaymentStatusAction(
  updateInvoicePaymentStatus: ReturnType<typeof useUpdateInvoicePaymentStatus>,
  messages: ActionMessages,
): Pick<InvoiceWorkspaceController, 'updatePaymentStatus' | 'paymentPendingIds'> {
  const pending = usePendingIdSet();

  return {
    paymentPendingIds: pending.ids,
    updatePaymentStatus: (invoice, isPaid) => {
      void updateInvoicePaymentStatusSelected(
        invoice,
        isPaid,
        updateInvoicePaymentStatus.mutateAsync,
        pending.hasPending,
        pending.setPending,
        messages,
      );
    },
  };
}

async function updateInvoicePaymentStatusSelected(
  invoice: InvoiceListItem,
  isPaid: boolean,
  updateInvoicePaymentStatus: (input: { id: string; isPaid: boolean }) => Promise<unknown>,
  hasPending: (id: string) => boolean,
  setPending: (id: string, pending: boolean) => void,
  messages: ActionMessages,
): Promise<void> {
  if (hasPending(invoice.id)) return;
  setPending(invoice.id, true);
  messages.clearActionState();
  try {
    await updateInvoicePaymentStatus({ id: invoice.id, isPaid });
    messages.setActionMessage('Payment status updated.');
  } catch {
    messages.setActionError(INVOICE_PAYMENT_STATUS_ERROR);
  } finally {
    setPending(invoice.id, false);
  }
}

function usePendingIdSet(): {
  ids: ReadonlySet<string>;
  hasPending: (id: string) => boolean;
  setPending: (id: string, pending: boolean) => void;
} {
  const idsRef = useRef<Set<string>>(new Set());
  const [ids, setIds] = useState<ReadonlySet<string>>(() => new Set());

  return {
    ids,
    hasPending: (id) => idsRef.current.has(id),
    setPending: (id, pending) => {
      const next = new Set(idsRef.current);
      if (pending) next.add(id);
      else next.delete(id);
      idsRef.current = next;
      setIds(next);
    },
  };
}

function useSanitizedInvoiceWorkspaceSearch(
  rawSearch: Record<string, unknown>,
  filters: InvoiceWorkspaceSearch,
  navigate: ReturnType<typeof useNavigate>,
): void {
  useEffect(() => {
    if (!hasUnsafeSearch(rawSearch, filters)) return;
    void navigate({ to: '/' as never, search: sanitizeSearch(filters) as never, replace: true });
  }, [filters, navigate, rawSearch]);
}

async function safeLoadPdfSignature(
  signaturePreviewUrl: string | null,
): ReturnType<typeof loadPdfSignature> {
  try {
    return await loadPdfSignature(signaturePreviewUrl);
  } catch {
    return null;
  }
}

function InvoiceWorkspaceFilters({
  filters,
  search,
  onSearchChange,
  onFiltersChange,
}: {
  filters: InvoiceWorkspaceSearch;
  search: string;
  onSearchChange: (value: string) => void;
  onFiltersChange: (filters: Partial<InvoiceWorkspaceSearch>) => void;
}): React.ReactElement {
  return (
    <section className="grid gap-4 md:grid-cols-[minmax(18rem,1fr)_minmax(10rem,12rem)_minmax(9rem,10rem)_minmax(9rem,10rem)]">
      <SearchFilter value={search} onChange={onSearchChange} />
      <CurrencyFilter value={filters.currency} onChange={onFiltersChange} />
      <YearFilter value={filters.year} onChange={onFiltersChange} />
      <PageSizeFilter value={filters.pageSize} onChange={onFiltersChange} />
    </section>
  );
}

function SearchFilter({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}): React.ReactElement {
  return (
    <FilterField id="invoice-search" label="Search">
      <input
        id="invoice-search"
        type="search"
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
        className={inputFieldClassName}
      />
    </FilterField>
  );
}

function CurrencyFilter({
  value,
  onChange,
}: {
  value?: Currency;
  onChange: (filters: Partial<InvoiceWorkspaceSearch>) => void;
}): React.ReactElement {
  return (
    <FilterField id="invoice-currency" label="Currency">
      <NativeSelect
        id="invoice-currency"
        value={value ?? ''}
        onChange={(event) =>
          onChange({
            currency: event.currentTarget.value
              ? (event.currentTarget.value as Currency)
              : undefined,
          })
        }
      >
        <option value="">All</option>
        <option value="EUR">EUR</option>
        <option value="USD">USD</option>
        <option value="RUB">RUB</option>
      </NativeSelect>
    </FilterField>
  );
}

function YearFilter({
  value,
  onChange,
}: {
  value?: number;
  onChange: (filters: Partial<InvoiceWorkspaceSearch>) => void;
}): React.ReactElement {
  return (
    <FilterField id="invoice-year" label="Year">
      <input
        id="invoice-year"
        type="number"
        value={value ?? ''}
        min={1900}
        max={9999}
        onChange={(event) =>
          onChange({
            year: event.currentTarget.value ? Number(event.currentTarget.value) : undefined,
          })
        }
        className={inputFieldClassName}
      />
    </FilterField>
  );
}

function PageSizeFilter({
  value,
  onChange,
}: {
  value: number;
  onChange: (filters: Partial<InvoiceWorkspaceSearch>) => void;
}): React.ReactElement {
  return (
    <FilterField id="invoice-page-size" label="Page size">
      <NativeSelect
        id="invoice-page-size"
        value={String(value)}
        onChange={(event) => onChange({ pageSize: Number(event.currentTarget.value) })}
      >
        <option value="25">25</option>
        <option value="50">50</option>
        <option value="100">100</option>
      </NativeSelect>
    </FilterField>
  );
}

function FilterField({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="space-y-1.5">
      <label
        className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-ink-muted"
        htmlFor={id}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function ListResults({
  filters,
  actionBusy,
  onOpen,
  onDuplicate,
  onDelete,
  onDownload,
  onPaymentStatusChange,
  downloadPendingIds,
  paymentPendingIds,
}: InvoiceWorkspaceResultsProps): React.ReactElement {
  const query = useInvoices(toListFilters(filters));
  return (
    <InvoiceWorkspaceResultsView
      response={query.data}
      loading={query.isPending}
      error={query.isError}
      searching={false}
      actionBusy={actionBusy}
      onOpen={onOpen}
      onDuplicate={onDuplicate}
      onDelete={onDelete}
      onDownload={onDownload}
      onPaymentStatusChange={onPaymentStatusChange}
      downloadPendingIds={downloadPendingIds}
      paymentPendingIds={paymentPendingIds}
    />
  );
}

function SearchResults({
  filters,
  q,
  actionBusy,
  onOpen,
  onDuplicate,
  onDelete,
  onDownload,
  onPaymentStatusChange,
  downloadPendingIds,
  paymentPendingIds,
}: InvoiceWorkspaceResultsProps & { q: string }): React.ReactElement {
  const query = useInvoiceSearch({ q, ...toListFilters(filters) });
  return (
    <InvoiceWorkspaceResultsView
      response={query.data}
      loading={query.isPending}
      error={query.isError}
      searching
      actionBusy={actionBusy}
      onOpen={onOpen}
      onDuplicate={onDuplicate}
      onDelete={onDelete}
      onDownload={onDownload}
      onPaymentStatusChange={onPaymentStatusChange}
      downloadPendingIds={downloadPendingIds}
      paymentPendingIds={paymentPendingIds}
    />
  );
}

interface InvoiceWorkspaceResultsProps {
  filters: InvoiceWorkspaceSearch;
  actionBusy: boolean;
  onOpen: (invoice: InvoiceListItem) => void;
  onDuplicate: (invoice: InvoiceListItem) => void;
  onDelete: (invoice: InvoiceListItem) => void;
  onDownload: (invoice: InvoiceListItem) => void;
  onPaymentStatusChange: (invoice: InvoiceListItem, isPaid: boolean) => void;
  downloadPendingIds: ReadonlySet<string>;
  paymentPendingIds: ReadonlySet<string>;
}

function InvoiceWorkspaceResults({
  filters,
  q,
  controller,
}: {
  filters: InvoiceWorkspaceSearch;
  q: string;
  controller: InvoiceWorkspaceController;
}): React.ReactElement {
  const resultProps: InvoiceWorkspaceResultsProps = {
    filters,
    actionBusy: controller.actionBusy,
    onOpen: controller.openInvoice,
    onDuplicate: controller.duplicateSelected,
    onDelete: controller.requestDelete,
    onDownload: controller.downloadSelected,
    onPaymentStatusChange: controller.updatePaymentStatus,
    downloadPendingIds: controller.downloadPendingIds,
    paymentPendingIds: controller.paymentPendingIds,
  };

  return q ? <SearchResults {...resultProps} q={q} /> : <ListResults {...resultProps} />;
}

interface InvoiceWorkspaceResultsViewProps {
  response?: InvoiceListResponse;
  loading: boolean;
  error: boolean;
  searching: boolean;
  actionBusy: boolean;
  onOpen: (invoice: InvoiceListItem) => void;
  onDuplicate: (invoice: InvoiceListItem) => void;
  onDelete: (invoice: InvoiceListItem) => void;
  onDownload: (invoice: InvoiceListItem) => void;
  onPaymentStatusChange: (invoice: InvoiceListItem, isPaid: boolean) => void;
  downloadPendingIds: ReadonlySet<string>;
  paymentPendingIds: ReadonlySet<string>;
}

function InvoiceWorkspaceResultsView({
  response,
  loading,
  error,
  searching,
  actionBusy,
  onOpen,
  onDuplicate,
  onDelete,
  onDownload,
  onPaymentStatusChange,
  downloadPendingIds,
  paymentPendingIds,
}: InvoiceWorkspaceResultsViewProps): React.ReactElement {
  const invoices = response?.invoices ?? [];

  if (loading) return <p className="text-sm text-ink-muted">Loading…</p>;
  if (error) return <StatusMessage tone="error">{INVOICE_LOAD_ERROR}</StatusMessage>;
  if (invoices.length === 0) {
    return searching ? (
      <>
        {response ? <PaymentTotalsSummary totals={response.totals.byCurrency} /> : null}
        <EmptyState>No invoices found.</EmptyState>
      </>
    ) : (
      <>
        {response ? <PaymentTotalsSummary totals={response.totals.byCurrency} /> : null}
        <EmptyState withAction>No invoices yet. Create your first one.</EmptyState>
      </>
    );
  }

  return (
    <>
      {response ? <PaymentTotalsSummary totals={response.totals.byCurrency} /> : null}
      <InvoiceHistoryList
        invoices={invoices}
        actionBusy={actionBusy}
        onOpen={onOpen}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onDownloadPdf={onDownload}
        onPaymentStatusChange={onPaymentStatusChange}
        downloadPendingIds={downloadPendingIds}
        paymentPendingIds={paymentPendingIds}
      />
      {response ? <InvoicePaginationSummary response={response} /> : null}
    </>
  );
}

function PaymentTotalsSummary({ totals }: { totals: InvoiceCurrencyTotals[] }): React.ReactElement {
  const displayTotals = totals.length > 0 ? totals : EMPTY_PAYMENT_TOTALS;

  return (
    <section
      aria-label="Payment totals"
      className="overflow-hidden rounded-2xl border border-line bg-paper-soft px-5 py-5 shadow-[0_10px_30px_-26px_rgba(31,27,21,0.5)] sm:px-6 sm:py-6"
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-ink-muted">
            Totals
          </span>
          <span className="font-serif text-xs italic text-ink-muted">across this view</span>
        </div>
        <div className="grid gap-5 border-t border-line-soft pt-4 sm:grid-cols-2 sm:gap-10">
          <PaymentTotalsAmountRow
            label="Invoiced"
            totals={displayTotals}
            amountKey="invoicedCents"
          />
          <PaymentTotalsAmountRow label="Paid" totals={displayTotals} amountKey="paidCents" />
        </div>
      </div>
    </section>
  );
}

const EMPTY_PAYMENT_TOTALS: InvoiceCurrencyTotals[] = [
  { currency: 'EUR', invoicedCents: 0, paidCents: 0 },
];

type PaymentTotalsAmountKey = 'invoicedCents' | 'paidCents';

function PaymentTotalsAmountRow({
  label,
  totals,
  amountKey,
}: {
  label: 'Invoiced' | 'Paid';
  totals: InvoiceCurrencyTotals[];
  amountKey: PaymentTotalsAmountKey;
}): React.ReactElement {
  const accent = label === 'Paid' ? 'text-sage' : 'text-ink';
  return (
    <div role="group" aria-label={`${label} totals`} className="flex flex-col gap-2">
      <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-ink-muted">
        {label}
      </p>
      <div className={`min-w-0 space-y-0.5 font-serif text-2xl tabular-nums ${accent}`}>
        {totals.map((total) => (
          <p key={`${total.currency}-${amountKey}`} className="break-words leading-tight">
            {formatCurrencyCents(total[amountKey], total.currency)}
          </p>
        ))}
      </div>
    </div>
  );
}

function InvoicePaginationSummary({
  response,
}: {
  response: InvoiceListResponse;
}): React.ReactElement {
  return (
    <p className="text-[11px] uppercase tracking-[0.16em] text-ink-muted">
      Page {response.pagination.page} of {Math.max(response.pagination.totalPages, 1)} ·{' '}
      {response.pagination.total} total
    </p>
  );
}

function EmptyState({
  children,
  withAction,
}: {
  children: React.ReactNode;
  withAction?: boolean;
}): React.ReactElement {
  return (
    <div className="flex flex-col items-center gap-4 rounded-2xl border border-dashed border-line bg-paper-soft/60 px-6 py-12 text-center">
      <p className="font-serif text-lg italic text-ink-soft">{children}</p>
      {withAction ? (
        <Link
          to="/invoices/new"
          className="inline-flex min-h-11 items-center justify-center rounded-full bg-ochre px-5 py-2 text-sm font-semibold text-paper-soft shadow-[0_8px_20px_-12px_rgba(176,119,43,0.85)] transition hover:bg-ochre-deep"
        >
          New invoice
        </Link>
      ) : null}
    </div>
  );
}

function toListFilters(filters: InvoiceWorkspaceSearch): InvoiceListFilters {
  return {
    currency: filters.currency,
    year: filters.year,
    page: filters.page,
    pageSize: filters.pageSize,
  };
}

function sanitizeSearch(filters: InvoiceWorkspaceSearch): InvoiceWorkspaceSearch {
  return validateInvoiceWorkspaceSearch(filters as unknown as InvoiceWorkspaceSearchInput);
}

function hasUnsafeSearch(
  rawSearch: Record<string, unknown>,
  filters: InvoiceWorkspaceSearch,
): boolean {
  if (Object.keys(rawSearch).some((key) => !allowedInvoiceWorkspaceSearchKeys.has(key))) {
    return true;
  }
  return (
    (rawSearch.currency !== undefined && rawSearch.currency !== filters.currency) ||
    (rawSearch.year !== undefined && rawSearch.year !== filters.year) ||
    (rawSearch.page !== undefined && rawSearch.page !== filters.page) ||
    (rawSearch.pageSize !== undefined && rawSearch.pageSize !== filters.pageSize)
  );
}

const inputFieldClassName =
  'block min-h-11 w-full rounded-lg border border-line bg-paper-soft px-3 py-2 text-sm text-ink shadow-[inset_0_1px_0_rgba(255,255,255,0.4)] outline-none transition placeholder:text-ink-muted focus:border-ochre focus:ring-2 focus:ring-ochre/30';

const allowedInvoiceWorkspaceSearchKeys = new Set(['currency', 'year', 'page', 'pageSize']);
